import { del, get, getBlob, post } from "../utils/FetchV2.util";
import { logger } from "../utils/logger";
import { requestDownloadLink, type DownloadLink } from "./downloadLink.service";

export interface FiscalValidator {
  rfc: string;
  nombre: string;
  domicilio_fiscal: string;
  regimen_fiscal: string;
  uso_cfdi: string;
  codigo: string;
  significado_codigo: string;
  descripcion: string;
}

export interface ValidationPropsFields {
  rfc: string;
  name: string;
  postal_code: string;
  tax_regime: string;
  cfdiUse?: string;
}

export const getUserValidationFieldsService = async ({
  props,
}: {
  props: ValidationPropsFields;
}): Promise<FiscalValidator> => {
  try {
    const { rfc, name, postal_code, tax_regime, cfdiUse } = props;
    const params = new URLSearchParams({
      rfc,
      nombre: name,
      domicilio_fiscal: postal_code,
      regimen_fiscal: tax_regime,
    });
    if (cfdiUse) params.set("uso_cfdi", cfdiUse);

    const response = await get({
      service: "fiscalEngine",
      endpoint: `/taxpayer/cfdi-fiscal-validator?${params.toString()}`,
    });

    return response;
  } catch (error) {
    logger.error("getUserValidationFields - Error:", error);
    throw error;
  }
};

/**
 * `year`/`month` cortan en UTC; `from`/`to` (AAAA-MM-DD, inclusivos) cortan en
 * Ciudad de México y no se combinan con los otros dos. `emittedOnly` no trae las
 * recibidas.
 */
export const getUnifiedInvoicesService = async ({
  year,
  month,
  from,
  to,
  emittedOnly,
}: {
  year?: number;
  month?: number;
  from?: string;
  to?: string;
  emittedOnly?: boolean;
}): Promise<any> => {
  const params = new URLSearchParams();
  if (year) params.set("year", String(year));
  if (year && month) params.set("month", String(month));
  if (from && to) {
    params.set("from", from);
    params.set("to", to);
  }
  if (emittedOnly) params.set("emitted_only", "true");
  const query = params.toString();
  try {
    const response = await get({
      service: "fiscalEngine",
      endpoint: `/invoices/profile/invoices-unified${query ? `?${query}` : ""}`,
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export interface RetentionIncomeBasedResponse {
  uuid: string;
  total_taxable_amount: number | string;
  total_retained_amount: number | string | null;
  total_exempt_amount: number | string | null;
  issuer_name: string;
  issuer_code: string;
  receiver_name: string;
  receiver_code: string;
  period_from: string;
  period_to: string;
  issued_at: string;
  cancelled_at: string | null;
  service_type?: string;
  xml_url?: string;
  pdf_url?: string;
}

export type RetentionStatusFilter = "vigente" | "cancelado";

/**
 * The PDF of a CFDI of the user's RFC, painted on the fly from its stamped XML by the
 * fiscal-engine (`mifacturacion`): the only PDF an invoice stamped outside heru has.
 */
export const getInvoicePdfFromXmlService = (uuid: string): Promise<Blob> =>
  getBlob({
    service: "fiscalEngine",
    endpoint: `/web/mi-facturacion/facturas/${encodeURIComponent(uuid)}/pdf`,
  });

export type FiscalDownloadLinkRequest =
  | { kind: "zubale_certificate" | "didi_certificate" | "rappi_certificate"; ref: Record<string, never> }
  | { kind: "cfdi_stamped" | "mi_facturacion" | "declaration_invoice"; ref: { uuid: string; format: "pdf" | "xml" } }
  | { kind: "declaration_document"; ref: { operation: string; document: string } }
  | { kind: "qr_poster"; ref: { headline?: string; subheadline?: string } };

/**
 * A short-lived link to a file the fiscal-engine builds when the link is opened
 * (a CFDI, a declaration document, the Zubale or DiDi certificate, the QR poster). The
 * native app cannot save a `blob:`, so the system browser opens this link instead.
 * Answers 404 (not found or not the user's), 409 `ZUBALE_CERTIFICATE_NOT_ELIGIBLE`
 * and 503.
 */
export const createFiscalDownloadLinkService = ({ kind, ref }: FiscalDownloadLinkRequest): Promise<DownloadLink> =>
  requestDownloadLink("fiscalEngine", "/downloads/links", { kind, ref });

export const getRetentionsByPeriodService = async (
  period: string,
  status?: RetentionStatusFilter
): Promise<RetentionIncomeBasedResponse[]> => {
  try {
    const statusParam = status ? `&status=${status}` : "";
    const response = await get({
      service: "fiscalEngine",
      endpoint: `/invoices/profile/retentions-by-period?period=${period}${statusParam}`,
    });

    return Array.isArray(response) ? response : [];
  } catch (error) {
    logger.error("getRetentionsByPeriodService - Error:", error);
    throw error;
  }
};

// ============================================
// TYPES & INTERFACES
// ============================================

export interface SavedProduct {
  id: number;
  key_prod_serv: string;
  quantity: number;
  key_unity: string;
  description: string;
  unitary_value: number;
  discount: number;
  amount: number;
  user_id: number;
  iva: number;
  has_retentions: boolean;
  iva_exempt: boolean;
}

export interface SaveProductPayload {
  key_prod_serv: string;
  quantity: number;
  key_unity: string;
  description: string;
  unitary_value: number;
  discount: number;
  amount: number;
  user_id: number;
  iva: number;
  has_retentions: boolean;
  iva_exempt: boolean;
  retentions_data?: string | null;
  regime_from?: string;
  regime_to?: string;
}

// ============================================
// PRODUCTS (User Saved Products)
// ============================================

export const getMyProductsService = async (): Promise<SavedProduct[]> => {
  try {
    const response = await get({
      service: "fiscalEngine",
      endpoint: `/v2/users/products`,
    });

    // Transform camelCase response to snake_case for compatibility
    const products = Array.isArray(response)
      ? response
      : response.products || [];
    return products.map((p: any) => ({
      id: p.id,
      key_prod_serv: p.keyProdServ,
      quantity: p.quantity,
      key_unity: p.keyUnity,
      description: p.description,
      unitary_value: p.unitaryValue,
      discount: p.discount,
      amount: p.amount,
      user_id: p.userId,
      iva: p.iva,
      has_retentions: p.hasRetentions,
      iva_exempt: p.ivaExempt,
      retentionsData: p.retentionsData,
    }));
  } catch (error) {
    throw error;
  }
};

export const saveMyProductService = async (
  payload: Omit<SaveProductPayload, "user_id">
): Promise<any> => {
  try {
    const camelCasePayload = {
      keyProdServ: payload.key_prod_serv,
      quantity: payload.quantity,
      keyUnity: payload.key_unity,
      description: payload.description,
      unitaryValue: payload.unitary_value,
      discount: payload.discount,
      amount: payload.amount,
      iva: payload.iva,
      hasRetentions: payload.has_retentions,
      ivaExempt: payload.iva_exempt,
      retentionsData: payload.retentions_data,
      regimeFrom: payload.regime_from,
      regimeTo: payload.regime_to,
    };

    const response = await post({
      service: "fiscalEngine",
      endpoint: `/v2/users/products`,
      body: camelCasePayload,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteMyProductService = async (
  productId: number
): Promise<any> => {
  try {
    const response = await del({
      service: "fiscalEngine",
      endpoint: `/v2/users/products/${productId}`,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getTaxpayerService = async (): Promise<any> => {
  try {
    const response = await get({
      service: "fiscalEngine",
      endpoint: `/taxpayer/info`,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const SATUnlinkedByUserService = async () => {
  try {
    const response = await del({
      service: "fiscalEngine",
      endpoint: `/credentials/web/unlink`,
    });
    return response;
  } catch (error) {
    throw error;
  }
};
